<template>
  <div id="app" :class="{ 'logged-in': loggedIn }">
    <div v-if="loggedIn">
      <app-header></app-header>
      <app-navigation></app-navigation>
      <transition name="slideIn">
        <quick-link-menu v-if="isLinkOpen" />
      </transition>
      <hero-slider-multi></hero-slider-multi>
      <!-- the-countdown-timer
        :end-time="countdownEnd"
        v-if="!$route.meta.isAdmin"
      / -->
      <app-container></app-container>
      <app-nav-bar></app-nav-bar>
      <footer class="site-footer">
        <p>
          <img src="/assets/footer-logo.svg" alt="MFT Logo" />
        </p>
        <p>&copy; {{ year }} - Manchester University NHS Foundation Trust</p>
        <p>
          Intranet by
          <a href="http://helloevery1.com" rel="noopener">every1</a>
        </p>
      </footer>
    </div>
    <div v-else>
      <app-container></app-container>
    </div>

    <dialog-list></dialog-list>
    <notification-list></notification-list>
    <portal-target name="modal">
      <!-- portal for modal content -->
    </portal-target>
    <debug-bar v-if="debugMode"></debug-bar>
  </div>
</template>

<script>
import Vue from 'vue';

import globalComponents from './globalComponents';

import DebugBar from './components/DebugBar';
import DialogList from '@/components/DialogList';
import NotificationList from '@/components/NotificationList';
import HeroSliderMulti from '@/components/HeroSliderMulti';

import './icons/';
import { mapState } from 'vuex';

const QuickLinkMenu = () => import('@/components/QuickLinksMenu');
const TheCountdownTimer = () => import('@/components/TheCountdownTimer');

export default {
  name: 'app',

  data() {
    return {
      debugMode: false,
      year: new Date().getFullYear(),
      countdownStart: new Date('2021-09-08').getTime(),
      countdownEnd:
        new Date('2023-11-31T11:59:00').getTime() - new Date().getTime(),
      // countdownEnd: new Date('2022-07-26T10:28:00').getTime() - new Date().getTime(),
    };
  },

  created() {
    window.addEventListener('keyup', (e) => {
      if (e.key === 'Escape') {
        if (this.$store.state.panelOpen === true) {
          this.$store.dispatch('CLOSE_PANEL');
        }
      }
    });
  },

  computed: {
    ...mapState('navigation', ['isLinkOpen']),
    loggedIn() {
      return this.$store.state.auth.token;
    },
  },

  components: {
    DebugBar,
    DialogList,
    NotificationList,
    QuickLinkMenu,
    TheCountdownTimer,
    HeroSliderMulti,
  },
};
</script>

<style lang="scss">
@import url('~sanitize.css');
@import url('~flexboxgrid/css/flexboxgrid.css');

::-ms-clear {
  display: none;
}
::placeholder {
  color: #697884;
}
:-ms-input-placeholder {
  color: #697884;
}

::-ms-input-placeholder {
  color: #697884;
}

.multiselect__placeholder {
  color: #697884 !important; // accessibility
}

select::-ms-expand {
  display: none;
}
input[type='password']::-ms-reveal {
  display: none;
}

html {
  -ms-overflow-style: -ms-autohiding-scrollbar;
}

body {
  background: tint($colour-nhs-pale-grey, 60);
  font-family: $font-family-primary;
  color: $colour-nhs-dark-grey;

  padding: 0;
  margin: 0;
  -webkit-overflow-scrolling: touch !important;
  overflow: auto !important;
  height: 100% !important;
}

.u-padding-0,
.u-p-0 {
  padding: 0 !important;
}
.u-ta-left {
  text-align: left;
}
.u-ta-center {
  text-align: center;
}
.u-ta-right {
  text-align: right;
}
.u-pos-relative {
  position: relative;
}
.u-underline {
  text-decoration: underline;
}
.u-no-pad-sm {
  padding: 0;

  @media (min-width: 768px) {
    padding: inherit;
  }
}

.u-floating-button {
  position: fixed;
  bottom: 50px;
  bottom: calc(env(safe-area-inset-bottom) + 50px);
  left: 0;
  width: 100vw;
}

.u-show-mobile-only {
  display: inline-block;
  @media screen and (min-width: 768px) {
    display: none !important;
  }
}

.u-show-tablet-down {
  display: inline-block;
  @media screen and (min-width: 1025px) {
    display: none !important;
  }
}

@media screen and (max-width: 767px) {
  .u-hide-mobile {
    display: none !important;
  }
}

@media screen and (max-width: 1023px) {
  .u-hide-tablet {
    display: none !important;
  }
}

.no-word-break {
  word-break: initial;
}

#app {
  max-width: 100vw;
  overflow: hidden;
  margin-top: 70px;
  @media screen and (min-width: 640px) {
    max-width: 999vh;
    &.logged-in {
      margin-top: 140px;
      margin-left: 55px;
      background: linear-gradient(
          180deg,
          rgba(241, 244, 245, 1) 0%,
          rgba(241, 244, 245, 0.9444152661064426) 85%,
          rgba(241, 244, 245, 0) 100%
        ),
        url('/assets/page-bg.svg') bottom right no-repeat;
    }
  }
  @media screen and (min-width: 768px) {
    &.logged-in {
      margin-top: 80px;
    }
  }
  @media screen and (min-width: 1024px) {
    &.logged-in {
      margin-top: 140px;
    }
  }
}

a {
  color: $colour-nhs-blue;
  cursor: pointer;
}

hr {
  border: none;
  margin: 4em 0;
  height: 1px;
  background: rgba(0, 0, 0, 0.1);

  &.thin {
    margin: 2em 0;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition-duration: 0.3s;
  transition-property: opacity;
  transition-timing-function: ease;
}

.fade-enter,
.fade-leave-active {
  opacity: 0;
}

.slideIn-enter-active,
.slideIn-leave-active {
  transition: ease all 0.3s;
}

.slideIn-enter,
.slideIn-leave-active {
  transform: translateX(-100vw);
}

/* recommended css code for vue-svgicon */
.svg-icon {
  display: inline-block;
  width: 16px;
  height: 16px;
  color: inherit;
  vertical-align: middle;
  fill: none;
  stroke: currentColor;
}

.svg-fill {
  fill: currentColor;
  stroke: none;
}

.svg-up {
  /* default */
  transform: rotate(0deg);
}

.svg-right {
  transform: rotate(90deg);
}

.svg-down {
  transform: rotate(180deg);
}

.svg-left {
  transform: rotate(-90deg);
}

.noscroll {
  overflow: hidden;
}
.noscroll-imp {
  overflow: hidden !important;
}

.listless {
  list-style-type: none;
  margin: 0;
  padding: 0;

  li {
    list-style-type: none;
    line-height: 1.5;
  }

  &.inline {
    li {
      display: inline-block;
      margin: 0 10px 0 0;
    }
  }
}

.m-0 {
  margin: 0;
}

.end {
  padding-right: 0;
}

button {
  &.inline {
    background: transparent;
    border: none;
    padding: 0;
    margin: 0;
    display: inline;
    color: inherit;
    cursor: pointer;

    &:hover {
      span {
        text-decoration: underline;
      }
    }
  }
}

.td-none {
  text-decoration: none;
}

.u-list {
  line-height: 1.444;
  &--blue {
    list-style-type: none;
    li {
      &:before {
        content: '\2022';
        color: #005eb8;
        display: inline-block;
        width: 1em;
        margin-left: -1em;
      }
    }
  }
}
$directions: left, right, center, justify;

@each $direction in $directions {
  .ta-#{$direction} {
    text-align: #{$direction};
  }
}

$verticals: (
  top: 'flex-start',
  center: 'center',
  bottom: 'flex-end',
);

@each $lbl, $type in $verticals {
  .va-#{$lbl} {
    align-self: #{$type};
  }
}

$floats: (
  left: 'left',
  right: 'right',
  none: 'none',
);

@each $lbl, $dir in $floats {
  .u-float-#{$lbl} {
    float: #{$dir};
  }
}

$padd-directions: top, left, bottom, right;
$numbers: 0, 5, 10, 15, 20, 15, 30, 35, 40, 45, 50;
@each $dir in $padd-directions {
  @each $number in $numbers {
    .p-#{$dir}-#{$number} {
      padding-#{$dir}: #{$number}px !important; // i know
    }
    .m-#{$dir}-#{$number} {
      margin-#{$dir}: #{$number}px !important; // i know
    }
    .p-#{$number} {
      padding: #{$number}px !important; // i know
    }
    .m-#{$number} {
      margin: #{$number}px !important; // i know
    }
  }
}

.clearfix {
  zoom: 1;
  &:before,
  &:after {
    content: ' ';
    display: table;
  }

  &:after {
    clear: both;
  }

  @supports (display: flow-root) {
    zoom: inherit;
    display: flow-root;

    &:before,
    &:after {
      content: none;
    }
  }
}

.flex-row {
  display: flex;
  flex-direction: row;
  align-items: center;
}

.row {
  clear: both;
}

.site-footer {
  text-align: center;

  p:last-of-type {
    margin-bottom: 0;
    padding-bottom: 4em;
    padding-bottom: calc(4em + env(safe-area-inset-bottom));

    @media (min-width: 768px) {
      padding-bottom: 1em;
    }
  }
}
.screen-reader-text {
  border: 0;
  clip: rect(1px, 1px, 1px, 1px);
  clip-path: inset(50%);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  width: 1px;
  word-wrap: normal !important;
}

.cursor-pointer {
  cursor: pointer;
}

.relative {
  position: relative;
}

.text-white {
  color: white;
}

.text-mid-grey {
  color: $colour-nhs-mid-grey;
}

.flex {
  display: flex;
}
.flex-wrap {
  flex-wrap: wrap;
}
.space-even {
  justify-content: space-evenly;
}

.capitalize {
  text-transform: capitalize;
}

.space-between {
  justify-content: space-between;
}

.items-center {
  align-items: center;
}

.fadeup-enter-active,
.fadeup-leave-active {
  transition: all 0.3s 0.3s;
}
.fadeup-enter {
  transform: translateY(40px);
  opacity: 0;
}
.fadeup-enter-to {
  transform: translateY(0);
  opacity: 1;
}

.fadeup-leave-to {
  transition: all 0.3s 0.3s;
  transform: translateY(40px);
  opacity: 0;
}
.mb-12 {
  margin-bottom: 3rem;
}
</style>
